import * as React from 'react';
import { Component, deps, inject, observer } from '../lib/component';
import { LayoutContent } from '../components/layout/content';
import { classes, style } from 'typestyle';
import {em, important, percent, px, url, viewWidth} from 'csx';
import {
  center,
  centerCenter,
  centerJustified,
  horizontal,
  horizontallySpaced,
  startJustified,
  vertical,
  verticallySpaced,
  wrap
} from 'csstips';
import {PageHeader} from '../components/page-header';
import {mediaTablet, mediaMobileOnly} from '../theme/media';
import {colorBrand, colorContrast, colorSlate, colorSubtle, colorWhite} from '../theme/color';
import {transitionQuickEase} from '../theme/transition';
import {resetAnchorStyles} from '../theme/reset';
import {constantHeaderHeight} from '../theme/constant';
import {gradientDark} from '../theme/gradient';
import {zUnderLayer} from '../theme/z';
import MainWrapper from '../components/main-wrapper';
import ArtistGrid, {ConnectedArtistGrid} from '../components/artist/grid';
import findFilters from '../lib/findFilters';
import formatArtistLetter from '../lib/formatArtistLetter';

export interface ArtistsPageProps {

}

@inject(deps) @observer
export class ArtistsPage extends Component<ArtistsPageProps> {
  render() {
    const { loading, loadingContent, coverImage, title, artists, letter } = this.props.model.page.artists;
    const { preloadImage } = this.props.controller.image;

    const letters = letter !== '_'
      ? [letter]
      : ArtistsPage.data.others;

    return (
      <MainWrapper className={ArtistsPage.styles.container}>
        <PageHeader coverImage={coverImage} hideMobile={true}>
          <h1>{title}</h1>
        </PageHeader>
        {!loading && (
          <LayoutContent innerClassName={ArtistsPage.styles.innerContainer}>
            {letter && (
              <div className={classes(ArtistsPage.styles.letterGrid, ArtistsPage.styles.letterGridSmall)}>
                {
                  ArtistsPage.data.letters.map(l => (
                    <div key={l}>
                      <a href={`/browse/artists/${l}`} onClick={this.handleClickLink} className={classes(ArtistsPage.styles.letterItem, letter === l && ArtistsPage.styles.letterItemActive)}>
                        <div>
                          {formatArtistLetter(l)}
                        </div>
                      </a>
                    </div>
                  ))
                }
              </div>
            )}
            {!letter && (
              <div className={classes(ArtistsPage.styles.letterGrid, ArtistsPage.styles.letterGridLarge)}>
                {
                  ArtistsPage.data.letters.map(l => (
                    <div key={l}>
                      <a href={`/browse/artists/${l}`} onClick={this.handleClickLink} className={classes(ArtistsPage.styles.letterItem, letter === l && ArtistsPage.styles.letterItemActive)}>
                        <div>
                          {formatArtistLetter(l)}
                        </div>
                      </a>
                    </div>
                  ))
                }
              </div>
            )}
            {letter && (
              <ConnectedArtistGrid
                filters={findFilters({letters})}
                pagination={{size: 512, from: 0}}
              />
            )}
          </LayoutContent>
        )}
      </MainWrapper>
    )
  }

  handleClickLink = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.model.router.push((event.currentTarget as HTMLAnchorElement).pathname);
  };

  static data = {
    letters: ['_', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'],
    others: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '!', '?', '*', ' ', '@', '#', '(', ')', '_']
  };

  static styles = {
    container: style({
      position: 'relative'
    }),
    split: style(
      {
        ...horizontal,
        $nest: {
          '& h3': {
            color: colorSlate.toString()
          }
        },
      },
      mediaMobileOnly({ ...vertical })
    ),
    splitNarrow: style({
      ...vertical,
    }),
    main: style(
      mediaTablet({
        flex: 2,
        padding: px(20),
        height: percent(100),
        $nest: {
          '&> div': {
            margin: '0 0 40px 0'
          }
        }
      }),
      mediaMobileOnly({
        width: percent(100),
        padding: px(20),
        height: 'auto',
        flex: 'none'
      })
    ),
    mainNarrow: style({
      width: percent(100),
      height: 'auto',
      flex: 'none'
    }),
    aside: style(
      mediaTablet({
        flex: 1,
        padding: px(20),
        height: percent(100)
      }),
      mediaMobileOnly({
        width: percent(100),
        height: 'auto',
        flex: 'none'
      })
    ),
    asideNarrow: style({
      width: percent(100),
      height: 'auto',
      flex: 'none'
    }),
    tracks: style(),
    loadMore: style({
      ...centerCenter,
      padding: '20px'
    }),
    heading: style({
      margin: '30px 0 20px 0'
    }),
    hero: style({
      position: 'relative',
      width: percent(100),
      $nest: {
        '& h3': {
          background: colorSlate.darken(0.05).toString(),
          color: colorWhite.toString(),
          margin: 0,
          height: constantHeaderHeight,
          padding: '0 20px',
          lineHeight: constantHeaderHeight
        }
      }
    }),
    definition: style(
      mediaMobileOnly({ padding: '0 20px' })
    ),
    loadingOverlay: style({
      ...vertical,
      ...center,
      ...startJustified,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: zUnderLayer,
      padding: px(120),
      background: colorSlate.darken(0.05).fade(0.9).toString()
    }),
    carousel: style(
      { minHeight: px(210) },
      mediaMobileOnly({ height: viewWidth(60) }),
      mediaTablet({ height: viewWidth(18) })
    ),
    playlist: style(
      { flexShrink: 0, height: 'auto !important', minWidth: px(210) },
      mediaMobileOnly({ width: viewWidth(60) }),
      mediaTablet({ width: viewWidth(18)  })
    ),
    strap: style(
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(20)
      }),
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(60),
        $nest: {
          '&> div': {
            flex: 1,
            width: percent(50)
          }
        }
      })
    ),
    featured: style({
      ...vertical,
      ...verticallySpaced(30)
    }),
    grid: style({
      ...horizontal,
      ...wrap
    }),
    item: style(
      {
        display: 'block',
        position: 'relative',
      },
    ),
    itemFeatured: style(
      {
        ...horizontal,
        ...horizontallySpaced(30),
        color: colorSlate.toString(),
        $nest: {
          ...resetAnchorStyles(colorSlate.toString(), false),
          '&> div:first-child': {
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
          },
          '&> div:last-child': {
            flex: 1
          },
          '&> div:last-child > div': {
            fontWeight: 'bold'
          },
          '& p': {
            margin: '5px 0 0 0',
            maxWidth: em(60)
          },
          '&:hover > div:last-child > div': {
            textDecoration: 'underline'
          }
        }
      },
      mediaMobileOnly({
        $nest: {
          '&> div:first-child': {
            width: px(60),
            height: px(60),
          }
        }
      }),
      mediaTablet({
        $nest: {
          '&> div:first-child': {
            width: px(120),
            height: px(120)
          }
        }
      })),
    itemAZ: style(
      mediaMobileOnly({
        width: percent(50),
      }),
      mediaTablet({
        width: percent(100/6),
      })
    ),
    itemTile: style(
      {
        paddingTop: percent(100),
        display: 'block',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        $nest: {
          '&> div': {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: px(10),
            background: colorSlate.fade(0.5).toString(),
            fontSize: px(16),
            lineHeight: px(16),
            width: percent(100),
            fontWeight: 'bold',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'pre',
            color: colorWhite.toString()
          }
        }
      },
      mediaTablet({
        $nest: {
          '&> div': {
            fontSize: px(20)
          }
        }
      })
    ),
    innerContainer: style(
      {
        ...vertical
      },
      mediaMobileOnly({
        flexDirection: 'column-reverse'
      })
    ),
    loadingContainer: style({
      ...centerCenter,
      height: px(280),
    }),
    contentContainer: style(
      {
        $nest: {
          '& h3': {
            margin: '30px 0'
          }
        }
      }
    ),
    letterGrid: style(
      {
        ...horizontal,
        ...wrap,
        width: percent(100),
      },
    ),
    letterGridSmall: style(
      {
        background: colorSlate.darken(0.075).toString(),
        $nest: {
          '&> div': {
            width: percent(100 / 27),
            background: colorSlate.darken(0.05).toString()
          },
          '&> div > a > div': {
            fontSize: viewWidth(1.5)
          }
        }
      },
      mediaMobileOnly({
        ...centerJustified,
        $nest: {
          '&> div': {
            width: px(44),
          },
          '&> div > a > div': {
            fontSize: px(18)
          }
        }
      })
    ),
    letterGridLarge: style(
      {
        background: gradientDark,
        $nest: {
          '&> div': {
            width: percent(100 / 8),
            background: colorSlate.darken(0.05).toString()
          },
          '&> div > a > div': {
            fontSize: viewWidth(100 / 32),
          }
        }
      },
      mediaTablet({
        padding: constantHeaderHeight
      }),
      mediaMobileOnly({
        padding: '16px',
        $nest: {
          '&> div': {
            width: percent(100 / 3),
          },
          '&> div > a > div': {
            fontSize: viewWidth(100 / 12),
          }
        }
      })
    ),
    letterItem: style(
      {
        position: 'relative',
        display: 'block',
        width: percent(100),
        paddingTop: percent(100),
        background: colorSlate.toString(),
        color: colorContrast.toString(),
        transition: `all ${transitionQuickEase}`,
        $nest: {
          ...resetAnchorStyles(colorContrast.toString()),
          '&:hover': {
            background: colorSlate.darken(0.15).toString()
          },
          '&> div': {
            ...centerCenter,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            fontWeight: 'bold',
          }
        }
      },
      mediaMobileOnly({
        $nest: {
          '&> div': {
            fontSize: px(18)
          }
        }
      })
    ),
    letterItemActive: style({
      background: colorBrand.toString(),
      color: important(colorWhite.toString()),
      $nest: {
        ...resetAnchorStyles(colorWhite.toString()),
        '&:hover': {
          background: colorBrand.darken(0.15).toString()
        },
      }
    }),
    empty: style({
      ...centerCenter,
      height: px(120),
      fontWeight: 200,
      fontSize: px(18),
      color: colorSubtle.toString()
    })
  };
}
